import {createStackNavigator} from "@react-navigation/stack";
import {createDrawerNavigator} from '@react-navigation/drawer';
import {NavigationContainer, NavigationContainerRef} from "@react-navigation/native";
import React, {useMemo, useRef, useState} from "react";
import {Platform, View} from "react-native";
import {GestureHandlerRootView} from "react-native-gesture-handler";
import {Provider, useSelector} from "react-redux";
import {applyMiddleware, legacy_createStore as createStore} from "redux";
import {thunk} from 'redux-thunk';
import AppReducer from "./Models/App/AppReducer";
import IAppState from "./Models/App/IAppState";
import Splash from "./Screens/Splash";
import Login from "./Screens/Login";
import AppHeader from "./Components/AppHeader";
import Clients from "./Screens/Clients";
import DrawerMenu from "./Components/DrawerMenu";
import {EPage} from "./Models/EPage";
import Courier from "./Screens/Courier";
import OpenOrder from "./Screens/OpenOrder";
import Orders from "./Screens/Orders";
import OrderHistory from "./Screens/OrderHistory";
import InstallPwaPrompt from "./Components/InstallPwaPrompt";
import {WebMenuContext} from "./Components/WebMenuContext";
import WebSideMenu from "./Components/WebSideMenu";

function setHeader(navigation: any, route: any) {
    return {
        header: () => <AppHeader navigation={navigation} pageId={route}/>
    };
}

const store = createStore(AppReducer, applyMiddleware(thunk));

const Stack = createStackNavigator();
const Drawer = createDrawerNavigator();

const RootView = Platform.OS === 'web' ? View : GestureHandlerRootView;

function MyStack() {
    return (
        <Stack.Navigator>
            <Stack.Screen name="Splash" component={Splash} options={{headerShown: false}}/>
            <Stack.Screen name="Login" component={Login} options={({navigation}) => setHeader(navigation, EPage.login)}/>
            <Stack.Screen name="Clients" component={Clients} options={({navigation}) => setHeader(navigation, EPage.clients)}/>
            <Stack.Screen name="Courier" component={Courier} options={({navigation}) => setHeader(navigation, EPage.courier)}/>
            <Stack.Screen name="OpenOrder" component={OpenOrder} options={({navigation}) => setHeader(navigation, EPage.openOrder)}/>
            <Stack.Screen name="Orders" component={Orders} options={({navigation}) => setHeader(navigation, EPage.orders)}/>
            <Stack.Screen name="OrderHistory" component={OrderHistory} options={({navigation}) => setHeader(navigation, EPage.orderHistory)}/>
        </Stack.Navigator>
    );
}

/** Native iOS/Android — real drawer navigator. */
function NativeAppDrawer() {
    const user = useSelector((state: IAppState) => state.user.current);

    return (
        <Drawer.Navigator
            screenOptions={{
                headerShown: false,
                swipeEnabled: !!user,
                drawerPosition: 'right',
            }}
            drawerContent={({navigation}) => (
                <DrawerMenu navigation={navigation} nestedInDrawer />
            )}>
            <Drawer.Screen name="MyStack" component={MyStack}/>
        </Drawer.Navigator>
    );
}

/** Web/PWA — stack only + modal side menu (avoids RTL drawer bugs in Chrome Android). */
function WebAppNavigator() {
    const [menuOpen, setMenuOpen] = useState(false);
    const navigationRef = useRef<NavigationContainerRef<any>>(null);

    const menuApi = useMemo(
        () => ({
            open: () => setMenuOpen(true),
            close: () => setMenuOpen(false),
            visible: menuOpen,
        }),
        [menuOpen],
    );

    return (
        <WebMenuContext.Provider value={menuApi}>
            <NavigationContainer ref={navigationRef}>
                <MyStack />
            </NavigationContainer>
            <WebSideMenu
                navigation={{
                    navigate: (...args: any[]) => navigationRef.current?.navigate(...(args as [any])),
                }}
            />
        </WebMenuContext.Provider>
    );
}

function App() {
    return (
        <RootView style={{flex: 1}}>
            <Provider store={store}>
                {Platform.OS === 'web' ? (
                    <WebAppNavigator />
                ) : (
                    <NavigationContainer>
                        <NativeAppDrawer />
                    </NavigationContainer>
                )}
                <InstallPwaPrompt />
            </Provider>
        </RootView>
    );
}

export default App;
